/* 凌統（字公績） */
import { SG, K, gd, spMul, sigOf, landing, along } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, U, D, S } from '../define.ts';

export default defineHero({
  id: 'lingtong', name: '凌統', sub: '字公績', faction: 'wu', role: '戰士', weapon: 'staff',
  look: { wart: 'sanjie', wclass: 'pole', weaponName: '三節棍', ranged: false, head: 'headband', hair: '#2a1a14', fx: '#ffb060', c: { cape: '#7f2a24', band: '#e9c46a' } },
  skills: [
    ['旋風棍', 'slam', '三節棍震地，周圍 170 內的敵人受到 125 傷害，並在腳下震出一片水域 4 秒：水域裡的敵人移速 -20%（站在裡面算濕，甘寧的錦帆會擊飛）。', { x: { k: 'ltQuake', dmg: 125, r: 170, water: 4, slow: 0.2 }, aim: S(170), cd: [10, 9, 8, 7], mp: [50, 55, 60, 65], el: null }],
    ['斷後', 'blink', '撲到 600 內一名友將身旁，3 秒內替他承受 {40/50/60/70}% 傷害，期間自己防禦 +30。', { x: { k: 'rearguard', pct: [40, 50, 60, 70], dur: 3 }, aim: { kind: 'point', range: 600, area: 160 }, cd: [18, 16, 14, 12], mp: 70, el: null }],
    ['國士', 'passive', '生命越低攻速越高，生命 30% 以下達到上限 +{30/45/60/75}%；對甘寧傷害 +10%（父仇）。', { pv: {}, sig: 'patriot', as: [30, 45, 60, 75] }],
    ['小師橋', 'slam', '朝方向飛躍 600，可以帶著身旁一名友將一起跳；落地 {180/270/360} 範圍傷害並擊飛 0.75 秒；起跳處留下 3 秒「斷橋」擋住追兵。', { x: { k: 'bridge', dmg: [180, 270, 360], dist: 600, r: 220 }, aim: D(600, 220), cd: [100, 85, 70], mp: [120, 140, 160], el: 'earth', shout: '主公快走，統斷後！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    // v0.5.3.2：震地，留下緩速的水域
    ltQuake(u, a, X) {
      const sp = spMul(u);
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => this.damage(u, t, X.dmg * sp, { stagger: 0.25 }));
      this.zone(u, { x: u.x, y: u.y, r: X.r, t: X.water, tick: 0.25, dmg: 0, ground: true, look: 'water', el: null, slow: X.slow, name: a.sk.name });
      this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: '#5fc4e8' });
    },
    rearguard(u, a, X) {
      // 選 a.pt 附近最近的友將（沒有就選自己附近最危險的友將）
      const allies = this.units.filter((o) => o.alive && o.kind === 'hero' && o.team === u.team && o !== u && gd(u.x, u.y, o.x, o.y) <= 650);
      allies.sort((p, q) => gd(a.pt[0], a.pt[1], p.x, p.y) - gd(a.pt[0], a.pt[1], q.x, q.y));
      const t = allies[0];
      if (!t) { this.tell(u, '斷後：附近沒有友將', 'warn'); return; }
      const p = landing(u.x, u.y, t.x - Math.sign(t.x - u.x || 1) * 50, t.y);
      this.fx({ k: 'puff', x: u.x, y: u.y }); u.x = p[0]; u.y = p[1]; this.fx({ k: 'puff', x: u.x, y: u.y });
      t.guard = { by: u, until: this.t + X.dur, pct: X.pct / 100 };
      this.addBuff(u, { id: 'rearguard', t: X.dur, armor: 30 });
      this.tell(u, `斷後：替 ${t.name} 擋傷`, 'info');
    },
    bridge(u, a, X) {
      const from = [u.x, u.y], dir = a.dir, sp = spMul(u);
      const ally = this.units.filter((o) => o.alive && o.kind === 'hero' && o.team === u.team && o !== u && gd(u.x, u.y, o.x, o.y) <= 160)
        .sort((p, q) => p.hp / p.maxHp - q.hp / q.maxHp)[0];
      const dest = landing(u.x, u.y, ...along(u.x, u.y, dir, X.dist) as [number, number]);
      u.x = dest[0]; u.y = dest[1];
      if (ally) { const q = landing(dest[0], dest[1], dest[0] - dir[0] * 50, dest[1] - dir[1] * 50 * K); ally.x = q[0]; ally.y = q[1]; this.fx({ k: 'puff', x: ally.x, y: ally.y }); }
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => { this.damage(u, t, X.dmg * sp, { magic: true, spell: true, stagger: 0.4 }); if (t.alive && !t.structure) this.applyCC(u, t, { knockup: 0.75 }); });
      this.fx({ k: 'slam', x: u.x, y: u.y, col: '#d8a860' });
      // 斷橋：起跳處垂直於跳躍方向的一道溝
      const tx = -dir[1], ty = dir[0], h = 150;
      this.addWall([from[0] - tx * h, from[1] - ty * h * K, from[0] + tx * h, from[1] + ty * h * K], 40, 3, u.team, 'city');
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 斷後：友將受到的傷害分一部分給凌統
    hook('damage.before', 5, function (c) {
      const t = c.t, g = t.guard;
      if (!g || g.until <= this.t || !g.by.alive || g.by === t || c.o && c.o.guarded) return;
      const share = c.a * g.pct;
      c.a -= share;
      this.damage(c.src, g.by, share, Object.assign({}, c.o, { guarded: true, quiet: true }));
    }),
    // 國士：生命越低攻速越高
    hook('aura', 42, function () {
      for (const u of this.units) {
        const p = u.alive && u.kind === 'hero' ? sigOf(u, 'patriot') : null;
        if (!p) continue;
        const k = Math.max(0, Math.min(1, (1 - u.hp / u.maxHp) / 0.7));
        if (k > 0) this.addBuff(u, { id: 'patriot', t: 0.7, as: p.v('as') * k });
      }
    }),
    // 國士：父仇（對甘寧加傷）
    hook('damage.before', 37, function (c) { if (sigOf(c.own, 'patriot') && c.t.spec && c.t.spec.id === 'ganning') c.a *= 1.1; }),
    hook('revive', 73, function (u) { u.guard = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    ltQuake: ({ foeHeroes, w, u }) => foeHeroes(200) >= 1 || w.units.filter((o) => o.alive && o.team !== u.team && o.team < 2 && gd(u.x, u.y, o.x, o.y) < 170).length >= 3,
    rearguard: ({ w, u }) => w.units.some((o) => o.alive && o.kind === 'hero' && o.team === u.team && o !== u && o.hp / o.maxHp < 0.45 && gd(u.x, u.y, o.x, o.y) < 600),
    bridge: ({ hero, d, foeHeroes }) => hero && d < 620 && foeHeroes(700) >= 2,
  },
});
